import {
    convertKeysToCamelCase,
    convertTutorSnakeToCamelCase,
    handlePageCountCommon,
} from 'utils/utilCommon'
import { supabase } from '../axios.instance'
import { createSupabaseQuery } from '../common'
import { getTutorCountRPC } from '../rpc/rpc.api'

const table = 'tutor_info'
const tableMatching = 'program_instructor_matching'

/**
 * 강사 목록
 * @param search - URL 쿼리스트링 (예: "isYouth=Y&isEmployment=Y&search=홍길동")
 * @param regionId - 지역 ID
 * @param path - 경로 ('tutors' 또는 'data-table')
 * @returns {Promise<{data}|PostgrestFilterBuilder<*, *, GetResult<*, *, *, "*">[], *>|PostgrestFilterBuilder<*, *, GetResult<*, *, *, "*">[], *>|null>}
 */
export const fetchTutorList = async (search, regionId, path) => {
    try {
        const pageSize = 10
        // search는 '?'로 시작하는 쿼리스트링이거나 빈 문자열일 수 있음
        // URLSearchParams는 '?'를 자동으로 처리하므로 그대로 전달
        const searchParams = new URLSearchParams(search || '')
        const currentPage = Number(searchParams.get('page')) || 1

        // 디버깅: 전달받은 search 파라미터 확인
        console.log('🔍 fetchTutorList - search:', search)
        console.log('🔍 fetchTutorList - searchParams:', Object.fromEntries(searchParams.entries()))
        console.log('🔍 fetchTutorList - regionId (파라미터):', regionId)
        
        // 여러 필터 동시 적용 확인
        const activeFilters = []
        if (searchParams.get('isEmployment')) activeFilters.push(`isEmployment=${searchParams.get('isEmployment')}`)
        if (searchParams.get('isAdvancedInstructor')) activeFilters.push(`isAdvancedInstructor=${searchParams.get('isAdvancedInstructor')}`)
        if (searchParams.get('isCareerGapFemale')) activeFilters.push(`isCareerGapFemale=${searchParams.get('isCareerGapFemale')}`)
        if (searchParams.get('isForeign')) activeFilters.push(`isForeign=${searchParams.get('isForeign')}`)
        if (searchParams.get('hasDisability')) activeFilters.push(`hasDisability=${searchParams.get('hasDisability')}`)
        if (searchParams.get('isVeterans')) activeFilters.push(`isVeterans=${searchParams.get('isVeterans')}`)
        if (searchParams.get('isYouth')) activeFilters.push(`isYouth=${searchParams.get('isYouth')}`)
        if (searchParams.get('isRetiree')) activeFilters.push(`isRetiree=${searchParams.get('isRetiree')}`)
        if (searchParams.get('educationLevel')) activeFilters.push(`educationLevel=${searchParams.get('educationLevel')}`)
        if (searchParams.get('employmentType')) activeFilters.push(`employmentType=${searchParams.get('employmentType')}`)
        if (searchParams.get('employmentStatus')) activeFilters.push(`employmentStatus=${searchParams.get('employmentStatus')}`)
        console.log('🔍 활성화된 필터 개수:', activeFilters.length)
        console.log('🔍 활성화된 필터 목록:', activeFilters)

        // ===== RPC 함수 파라미터 매핑 =====
        // buildSupabaseChainFilter의 로직을 참고하여 모든 필터 파라미터를 올바르게 변환

        // 1. year_param: registration_year 추출 (기본값 2025)
        const startDate = searchParams.get('startDate')
        let yearParam = null
        if (startDate) {
            // startDate가 YYYYMMDD 형식이면 년도 추출
            yearParam = parseInt(startDate.substring(0, 4), 10)
        } else {
            // 기본값으로 2025 사용 (기존 buildSupabaseChainFilter 로직과 동일)
            yearParam = 2025
        }

        // 2. region_param: 지역 ID
        // URL 쿼리스트링의 regionId를 우선 확인하고, 없으면 함수 파라미터의 regionId 사용
        const urlRegionId = searchParams.get('regionId')
        const finalRegionId = urlRegionId || regionId
        
        console.log('🔍 fetchTutorList - urlRegionId:', urlRegionId)
        console.log('🔍 fetchTutorList - finalRegionId:', finalRegionId)
        
        // regionId가 'all'이 아닐 때만 RPC 파라미터에 포함
        // 'all'일 때는 null로 전달 (RPC 함수에서 모든 지역을 의미)
        const regionParam = finalRegionId && finalRegionId !== 'all' ? finalRegionId : null
        
        console.log('🔍 fetchTutorList - regionParam:', regionParam)

        // 3. is_employment_param: 'Y' -> '신규', 'N' -> '재고용'
        const isEmploymentValue = searchParams.get('isEmployment')
        let isEmploymentParam = null
        if (isEmploymentValue === 'Y') {
            isEmploymentParam = '신규'
        } else if (isEmploymentValue === 'N') {
            isEmploymentParam = '재고용'
        }

        // 4. is_advanced_instructor_param: 'Y' -> '중급강사', 'N' -> '초급강사', 'A' -> '고급강사'
        const isAdvancedInstructorValue = searchParams.get('isAdvancedInstructor')
        let isAdvancedInstructorParam = null
        if (isAdvancedInstructorValue === 'Y') {
            isAdvancedInstructorParam = '중급강사'
        } else if (isAdvancedInstructorValue === 'N') {
            isAdvancedInstructorParam = '초급강사'
        } else if (isAdvancedInstructorValue === 'A') {
            isAdvancedInstructorParam = '고급강사'
        }

        // 5-1. is_youth_param: 'Y' -> 'Y' (청년 여부)
        const isYouthValue = searchParams.get('isYouth')
        const isYouthParam = isYouthValue === 'Y' ? 'Y' : null

        // 5-2. is_retiree_param: 'Y' -> 'Y' (전문퇴직자 여부)
        const isRetireeValue = searchParams.get('isRetiree')
        const isRetireeParam = isRetireeValue === 'Y' ? 'Y' : null

        // 5. is_career_gap_female_param: 'Y' -> 'Y' (경력단절여성 여부)
        const isCareerGapFemaleValue = searchParams.get('isCareerGapFemale')
        const isCareerGapFemaleParam = isCareerGapFemaleValue === 'Y' ? 'Y' : null

        // 6. is_foreign_param: 'Y' -> 'Y'
        const isForeignValue = searchParams.get('isForeign')
        const isForeignParam = isForeignValue === 'Y' ? 'Y' : null

        // 6-1. has_disability_param: 'Y' -> 'Y' (장애여부)
        const hasDisabilityValue = searchParams.get('hasDisability')
        const hasDisabilityParam = hasDisabilityValue === 'Y' ? 'Y' : null

        // 6-2. is_veterans_param: 'Y' -> 'Y' (보훈여부)
        const isVeteransValue = searchParams.get('isVeterans')
        const isVeteransParam = isVeteransValue === 'Y' ? 'Y' : null

        // 7. education_level_param: 그대로 전달 ('고졸', '전문대졸', '대졸', '석사', '박사')
        const educationLevelParam = searchParams.get('educationLevel') || null

        // 8. employment_type_param: 그대로 전달 ('직접채용', '위촉')
        const employmentTypeParam = searchParams.get('employmentType') || null

        // 8-1. employment_status_param: 그대로 전달
        const employmentStatusParam = searchParams.get('employmentStatus') || null

        // 9. start_date_param: YYYYMMDD 형식 그대로 전달
        const startDateParam = startDate || null

        // 10. end_date_param: YYYYMMDD 형식 그대로 전달
        const endDateParam = searchParams.get('endDate') || null

        // 11. search_param: 검색어 그대로 전달
        const searchParam = searchParams.get('search') || null

        // 12. sort_param: 정렬 옵션 그대로 전달 ('createdDt', 'updatedDt')
        // sortparam이 없을 때 기본값 'createdDt' 전달
        const sortParam = searchParams.get('sort') || 'createdDt'

        // RPC 함수 파라미터 객체 생성
        // null 값은 제외하여 RPC 함수에 전달하지 않음
        const rpcParams = {}
        
        if (yearParam !== null) rpcParams.year_param = yearParam
        if (regionParam !== null) rpcParams.region_param = regionParam
        if (isEmploymentParam !== null) rpcParams.is_employment_param = isEmploymentParam
        if (isAdvancedInstructorParam !== null) rpcParams.is_advanced_instructor_param = isAdvancedInstructorParam
        if (isYouthParam !== null) rpcParams.is_youth_param = isYouthParam
        if (isRetireeParam !== null) rpcParams.is_retiree_param = isRetireeParam
        if (isCareerGapFemaleParam !== null) rpcParams.is_career_gap_female_param = isCareerGapFemaleParam
        if (isForeignParam !== null) rpcParams.is_foreign_param = isForeignParam
        if (hasDisabilityParam !== null) rpcParams.has_disability_param = hasDisabilityParam
        if (isVeteransParam !== null) rpcParams.is_veterans_param = isVeteransParam
        if (educationLevelParam !== null) rpcParams.education_level_param = educationLevelParam
        if (employmentTypeParam !== null) rpcParams.employment_type_param = employmentTypeParam
        if (employmentStatusParam !== null) rpcParams.employment_status_param = employmentStatusParam
        if (startDateParam !== null) rpcParams.start_date_param = startDateParam
        if (endDateParam !== null) rpcParams.end_date_param = endDateParam
        if (searchParam !== null) rpcParams.search_param = searchParam
        // sortParam은 기본값이 있으므로 항상 전달
        rpcParams.sort_param = sortParam

        // 디버깅: RPC 파라미터 확인
        console.log('📤 RPC 파라미터:', rpcParams)
        console.log('📤 RPC 파라미터 개수:', Object.keys(rpcParams).length)
        console.log('📤 전달된 필터 파라미터:', {
            year: rpcParams.year_param,
            region: rpcParams.region_param,
            employment: rpcParams.is_employment_param,
            advancedInstructor: rpcParams.is_advanced_instructor_param,
            youth: rpcParams.is_youth_param,
            retiree: rpcParams.is_retiree_param,
            careerGapFemale: rpcParams.is_career_gap_female_param,
            foreign: rpcParams.is_foreign_param,
            hasDisability: rpcParams.has_disability_param,
            isVeterans: rpcParams.is_veterans_param,
            educationLevel: rpcParams.education_level_param,
            employmentType: rpcParams.employment_type_param,
            employmentStatus: rpcParams.employment_status_param,
            startDate: rpcParams.start_date_param,
            endDate: rpcParams.end_date_param,
            search: rpcParams.search_param,
            sort: rpcParams.sort_param,
        })

        // RPC 함수 호출
        const { data: rpcData, error: rpcError } = await supabase.rpc('get_crypted_tutor_info_list', rpcParams)

        if (rpcError) {
            console.error('get_crypted_tutor_info_list RPC error:', rpcError)
            throw rpcError
        }

        // ===== 필터 카운트 정보 (기존 RPC 함수 사용) =====
        // getTutorCountRPC는 기존 형식을 유지하므로 변환 필요
        // finalRegionId를 사용하여 올바른 지역 정보 전달
        // getTutorCountRPC는 'eq.' 접두사를 제거하므로 'eq.값' 형식으로 전달
        // 변환된 파라미터 값들을 재사용 (isEmploymentParam, isAdvancedInstructorParam, isYouthParam, isRetireeParam, isCareerGapFemaleParam, hasDisabilityParam, isVeteransParam, employmentStatusParam)
        const filterParams = {
            region_id: finalRegionId && finalRegionId !== 'all' ? `eq.${finalRegionId}` : null,
            is_youth: isYouthParam ? `eq.${isYouthParam}` : null,
            is_retiree: isRetireeParam ? `eq.${isRetireeParam}` : null,
            is_rehired: searchParams.get('isRehired') ? `eq.${searchParams.get('isRehired')}` : null,
            is_career_gap_female: isCareerGapFemaleParam ? `eq.${isCareerGapFemaleParam}` : null,
            is_advanced_instructor: isAdvancedInstructorParam ? `eq.${isAdvancedInstructorParam}` : null,
            is_employment: isEmploymentParam ? `eq.${isEmploymentParam}` : null,
            is_foreign: isForeignParam ? `eq.${isForeignParam}` : null,
            has_disability: hasDisabilityParam ? `eq.${hasDisabilityParam}` : null,
            is_veterans: isVeteransParam ? `eq.${isVeteransParam}` : null,
            education_level: educationLevelParam ? `eq.${educationLevelParam}` : null,
            teacher_certification: searchParams.get('teacherCertification')
                ? `eq.${searchParams.get('teacherCertification')}`
                : null,
            teaching_experience_yn: searchParams.get('teachingExperienceYn')
                ? `eq.${searchParams.get('teachingExperienceYn')}`
                : null,
            is_language: searchParams.get('isLanguage') ? `eq.${searchParams.get('isLanguage')}` : null,
            use_language: searchParams.get('useLanguage') ? `eq.${searchParams.get('useLanguage')}` : null,
            employment_status: employmentStatusParam ? `eq.${employmentStatusParam}` : null,
            employment_type: searchParams.get('employmentType') || null,
            start_date: searchParams.get('startDate') ? `gte.${searchParams.get('startDate')}` : null,
            end_date: searchParams.get('endDate') ? `lte.${searchParams.get('endDate')}` : null,
        }

        // 디버깅: 필터 카운트 파라미터 확인
        console.log('📊 필터 카운트 파라미터:', filterParams)
        
        // 필터 카운트에 전달되는 활성 필터 확인
        const activeFilterCountParams = Object.entries(filterParams)
            .filter(([, value]) => value !== null)
            .map(([key, value]) => `${key}=${value}`)
        console.log('📊 필터 카운트에 전달되는 활성 필터:', activeFilterCountParams)
        console.log('📊 필터 카운트 활성 필터 개수:', activeFilterCountParams.length)

        let filter = await getTutorCountRPC(filterParams)
        filter = convertKeysToCamelCase(filter)

        // 디버깅: 필터 카운트 결과 확인
        console.log('📊 필터 카운트 결과:', filter)

        // ===== RPC 응답 데이터 처리 =====
        if (!rpcData || !Array.isArray(rpcData)) {
            return {
                data: {
                    filterCountInfo: filter || {},
                    tutorInfoList: [],
                    page: { totalCount: 0, totalPages: 0, currentPage: 1 },
                },
                status: 200,
            }
        }

        // data-table 경로인 경우 원본 데이터 반환
        if (path === 'data-table') {
            const convertedData = rpcData.map(tutor => {
                return convertTutorSnakeToCamelCase(tutor)
            })
            return {
                data: convertedData,
                status: 200,
            }
        }

        // snake → camel 변환
        const convertedData = rpcData.map(tutor => {
            return convertTutorSnakeToCamelCase(tutor)
        })

        // 클라이언트 측 페이지네이션 처리
        // RPC 함수가 전체 데이터를 반환하므로, 현재 페이지에 해당하는 데이터만 추출
        const startIndex = (currentPage - 1) * pageSize
        const endIndex = startIndex + pageSize
        const paginatedData = convertedData.slice(startIndex, endIndex)

        // handlePageCountCommon을 사용하기 위해 response 객체 생성
        // filter.totalCount를 count로 설정
        const responseForPagination = {
            count: filter?.totalCount || 0,
            data: paginatedData,
        }

        // handlePageCountCommon 함수 사용 (기존 로직과 동일)
        const paginationInfo = handlePageCountCommon(search, responseForPagination, pageSize)

        // 디버깅: 페이지네이션 정보 확인
        console.log('📄 페이지네이션 정보:', paginationInfo)
        console.log('📄 필터 totalCount:', filter?.totalCount)
        console.log('📄 전체 데이터 개수:', convertedData.length)
        console.log('📄 페이지네이션된 데이터 개수:', paginatedData.length)

        return {
            data: {
                filterCountInfo: filter || {},
                tutorInfoList: paginatedData,
                page: paginationInfo,
            },
            status: 200,
        }
    } catch (error) {
        console.error('fetchTutorList error:', error)
        return {
            data: {
                filterCountInfo: {},
                tutorInfoList: [],
                page: { totalCount: 0, totalPages: 0, currentPage: 1 },
            },
            error: error.message,
            status: 'error',
        }
    }
}

/**
 * 교육관리 강사매칭 강사목록
 * 등록/수정 페이지
 * RPC 함수를 사용하여 강사 목록 조회
 * @param search
 * @param regionId
 * @returns {Promise<{data: {tutorInfoList: *[]}, status: number}|{data: {tutorInfoList: *[]}, error: *, status: string}>}
 */
export const fetchMatchTutorList = async (search, regionId) => {
    try {
        // 현재 연도 추출
        const currentYear = new Date().getFullYear()

        // RPC 함수 파라미터 설정
        const rpcParams = {
            year_param: currentYear,
            sort_param: 'createdDt', // 기본 정렬
        }

        // 지역 조건 추가 (null이 아닐 때만 추가)
        const finalRegionId = regionId && regionId !== 'all' ? regionId : null
        if (finalRegionId) {
            rpcParams.region_param = finalRegionId
        }

        // RPC 함수 호출
        const { data: rpcData, error: rpcError } = await supabase.rpc('get_crypted_tutor_info_list', rpcParams)

        if (rpcError) {
            console.error('get_crypted_tutor_info_list RPC error:', rpcError)
            return {
                data: { tutorInfoList: [] },
                error: rpcError.message,
                status: 'error',
            }
        }

        // RPC 응답 데이터 처리
        if (!rpcData || !Array.isArray(rpcData)) {
            return {
                data: { tutorInfoList: [] },
                status: 200,
            }
        }

        // snake → camel 변환
        const convertedData = rpcData.map(tutor => {
            return convertTutorSnakeToCamelCase(tutor)
        })

        // tutor_id 기준 내림차순 정렬 (기존 로직 유지)
        convertedData.sort((a, b) => {
            const aId = a.tutorId || 0
            const bId = b.tutorId || 0
            return bId - aId
        })

        return {
            data: {
                tutorInfoList: convertedData,
            },
            status: 200,
        }
    } catch (error) {
        console.error('fetchMatchTutorList error:', error)
        return {
            data: { tutorInfoList: [] },
            error: error.message,
            status: 'error',
        }
    }
}

export const getTutorsById = async tutorId => {
    try {
        if (!tutorId) {
            throw new Error('Tutor ID is required')
        }

        // RPC 함수 호출
        const { data: rpcData, error: rpcError } = await supabase.rpc('get_crypted_tutor_info', {
            tutor_id_param: parseInt(tutorId, 10),
        })

        if (rpcError) {
            console.error('get_crypted_tutor_info RPC error:', rpcError)
            throw rpcError
        }

        if (!rpcData || (Array.isArray(rpcData) && rpcData.length === 0)) {
            return null
        }

        // 단일 객체 또는 배열의 첫 번째 요소 처리
        const tutorData = Array.isArray(rpcData) ? rpcData[0] : rpcData

        // snake → camel 변환
        const convertedData = convertTutorSnakeToCamelCase(tutorData)

        // program_instructor_matching 정보는 별도로 조회 (RPC에서 포함되지 않을 경우)
        const matchingResponse = await supabase
            .from(tableMatching)
            .select('*')
            .eq('tutor_id', tutorId)
            .eq('is_delete', false)

        if (matchingResponse.data && Array.isArray(matchingResponse.data)) {
            convertedData.programInstructorMatchingList = matchingResponse.data
        }

        return convertedData
    } catch (error) {
        console.error('getTutorsById error:', error)
        return null
    }
}

export const postTutors = async tutorList => {
    try {
        // tutorList가 null, undefined이거나 빈 배열/객체인 경우 체크
        if (
            !tutorList ||
            (Array.isArray(tutorList) && tutorList.length === 0) ||
            (typeof tutorList === 'object' && !Array.isArray(tutorList) && Object.keys(tutorList).length === 0)
        ) {
            throw new Error('Valid tutor list is required')
        }

        // 암호화 없이 직접 저장
        const response = await supabase.from(table).insert(tutorList).select()

        if (response.data && Array.isArray(response.data)) {
            // 원래 입력이 단건(객체)이었다면 첫 번째 요소만 반환
            if (!Array.isArray(tutorList)) {
                const [firstItem] = response.data
                response.data = firstItem
            }
        }

        return response
    } catch (error) {
        console.error('postTutors error:', error)
        return {
            data: null,
            error: error.message,
            status: 'error',
        }
    }
}

export const editTutor = async tutor => {
    try {
        if (!tutor || !tutor.tutor_id) {
            throw new Error('Valid tutor data with tutor_id is required')
        }

        // 암호화 없이 직접 업데이트
        const response = await supabase.from(table).update(tutor).eq('tutor_id', tutor.tutor_id).select()

        return response
    } catch (error) {
        console.error('editTutor error:', error)
        return {
            data: null,
            error: error.message,
            status: 'error',
        }
    }
}

/**
 * 수퍼베이스 삭제
 * is_delete(삭제여부) true로 변경
 * @param tutorId
 * @returns {Promise<*|null>}
 */
export const deleteTutorById = async tutorId => {
    try {
        if (!tutorId) {
            throw new Error('Tutor ID is required')
        }

        const [response] = await Promise.all([
            supabase.from(table).update({ is_delete: true }).eq('tutor_id', tutorId).select(),
            supabase.from(tableMatching).update({ is_delete: true }).eq('tutor_id', tutorId).select(),
        ])

        return response
    } catch (error) {
        console.error('deleteTutorById error:', error)
        return {
            data: null,
            error: error.message,
            status: 'error',
        }
    }
}

/**
 * DB에서 같은 지역 강사 동명이인 찾기 (배열)
 * RPC 함수 get_crypted_tutor_info_list를 사용하여 암호화된 데이터와 평문 데이터를 모두 검색
 * @param tutorNames - 강사명 배열
 * @param regionId - 지역 ID
 * @param tutorYearArray - 등록 연도 배열
 * @returns {Promise<{data: *[], error?: *, status?: string}>}
 */
export const findDuplicatedTutorNames = async (tutorNames, regionId, tutorYearArray) => {
    try {
        if (!Array.isArray(tutorNames) || tutorNames.length === 0) {
            return { data: [] }
        }

        // regionId 처리
        const finalRegionId = regionId && regionId !== 'all' ? regionId : null
        
        // 등록 연도 배열에서 첫 번째 연도를 year_param으로 사용 (RPC 함수 요구사항)
        // tutorYearArray가 있으면 첫 번째 연도 사용, 없으면 현재 연도
        const yearParam = Array.isArray(tutorYearArray) && tutorYearArray.length > 0 
            ? parseInt(tutorYearArray[0], 10) 
            : new Date().getFullYear()

        // 각 강사명에 대해 RPC 함수 호출
        const promises = tutorNames.map(async tutorName => {
            const rpcParams = {
                year_param: yearParam,
                search_param: tutorName, // 강사명으로 검색
                sort_param: 'createdDt',
            }

            // regionId가 있을 때만 추가
            if (finalRegionId) {
                rpcParams.region_param = finalRegionId
            }

            // 등록 연도 필터링을 위해 start_date_param 사용
            if (Array.isArray(tutorYearArray) && tutorYearArray.length > 0) {
                // 등록 연도 배열의 최소값과 최대값으로 범위 설정
                const years = tutorYearArray.map(y => parseInt(y, 10)).filter(y => !Number.isNaN(y))
                if (years.length > 0) {
                    const minYear = Math.min(...years)
                    const maxYear = Math.max(...years)
                    rpcParams.start_date_param = `${minYear}0101`
                    rpcParams.end_date_param = `${maxYear}1231`
                }
            }

            const { data: rpcData, error: rpcError } = await supabase.rpc('get_crypted_tutor_info_list', rpcParams)

            if (rpcError) {
                console.error(`get_crypted_tutor_info_list RPC error for ${tutorName}:`, rpcError)
                return []
            }

            if (!rpcData || !Array.isArray(rpcData)) {
                return []
            }

            // 등록 연도 필터링 (RPC 함수가 날짜 범위로만 필터링하므로 추가 필터링 필요)
            let filteredData = rpcData
            if (Array.isArray(tutorYearArray) && tutorYearArray.length > 0) {
                const yearSet = new Set(tutorYearArray.map(y => parseInt(y, 10)))
                filteredData = rpcData.filter(tutor => {
                    const tutorYear = tutor.registration_year ? parseInt(tutor.registration_year, 10) : null
                    return tutorYear && yearSet.has(tutorYear)
                })
            }

            return filteredData
        })

        const results = await Promise.all(promises)
        const flatResults = results.flat()

        // snake → camel 변환
        const convertedData = flatResults.map(tutor => {
            return convertTutorSnakeToCamelCase(tutor)
        })

        // 중복 제거 (tutorId 기준)
        const uniqueResults = convertedData.reduce((acc, current) => {
            const existing = acc.find(item => item.tutorId === current.tutorId)
            if (!existing) {
                acc.push(current)
            }
            return acc
        }, [])

        return { data: uniqueResults }
    } catch (error) {
        console.error('Error finding duplicated tutor names:', error)
        return {
            data: [],
            error: error.message,
            status: 'error',
        }
    }
}

/**
 * DB에서 같은 지역 강사 동명이인 찾기 (단건 등록)
 * @param tutorName
 * @param regionId
 * @param year
 * @returns {Promise<*|PostgrestFilterBuilder<*, *, GetResult<*, *, *, "*">[], *>>}
 */
export const findDuplicatedTutorName = async (tutorName, regionId, year) => {
    try {
        if (!tutorName) {
            return { data: [] }
        }

        // 평문으로 검색
        let query = createSupabaseQuery(table, regionId)
        query = query.select().eq('tutor_name', tutorName)

        if (year) {
            query = query.eq('registration_year', year)
        }

        const response = await query

        // 결과 변환
        const combinedData = []

        if (response.data && Array.isArray(response.data)) {
            combinedData.push(...response.data.map(convertTutorSnakeToCamelCase))
        }

        // 중복 제거 (tutorId 기준)
        const uniqueData = combinedData.reduce((acc, current) => {
            const existing = acc.find(item => item.tutorId === current.tutorId)
            if (!existing) {
                acc.push(current)
            }
            return acc
        }, [])

        return {
            data: uniqueData,
            error: null,
            status: 'success',
        }
    } catch (error) {
        console.error('findDuplicatedTutorName error:', error)
        return {
            data: [],
            error: error.message,
            status: 'error',
        }
    }
}

/**
 * 교육관리 강사매칭 강사목록 상세
 * @param id
 * @returns {Promise<*|PostgrestResponseSuccess<GetResult<any, any, R | unknown, "*">[]>|PostgrestResponseFailure>}
 */
export const handleGetTutor = async id => {
    try {
        if (!id) {
            throw new Error('Tutor ID is required')
        }

        // RPC 함수 호출
        const { data: rpcData, error: rpcError } = await supabase.rpc('get_crypted_tutor_info', {
            tutor_id_param: parseInt(id, 10),
        })

        if (rpcError) {
            console.error('get_crypted_tutor_info RPC error:', rpcError)
            return {
                data: [],
                error: rpcError.message,
                status: 'error',
            }
        }

        if (!rpcData || (Array.isArray(rpcData) && rpcData.length === 0)) {
            return {
                data: [],
                error: null,
                status: 'success',
            }
        }

        // 단일 객체 또는 배열의 첫 번째 요소 처리
        const tutorData = Array.isArray(rpcData) ? rpcData[0] : rpcData

        // RPC 함수가 이미 복호화된 데이터를 반환하므로 복호화 불필요
        return {
            data: [tutorData],
            error: null,
            status: 'success',
        }
    } catch (error) {
        console.error('handleGetTutor error:', error)
        // 오류 객체 대신 구조화된 응답 반환
        return {
            data: [],
            error: error.message,
            status: 'error',
        }
    }
}

/**
 * 월별 강사정보 요약 통계 조회 (chart 용)
 * @param month 1~12월 (숫자)
 */
export const getMonthlyTutorChart = async month => {
    try {
        if (!month || month < 1 || month > 12) {
            throw new Error('Valid month (1-12) is required')
        }

        const { data, error } = await supabase.rpc('get_month_total_tutor', {
            month_param: month,
        })

        if (error) {
            console.error('get_month_total_tutor 에러:', error.message)
            return {
                data: [],
                error: error.message,
                status: 'error',
            }
        }

        return data ?? []
    } catch (error) {
        console.error('getMonthlyTutorChart 호출 실패:', error)
        return {
            data: [],
            error: error.message,
            status: 'error',
        }
    }
}

export const getTutorHistory = async tutorId => {
    try {
        const { data, error } = await supabase.rpc('get_audit_log', {
            category_param: 'tutor',
            id_param: tutorId,
        })

        if (error) {
            console.error('getTutorHistory 호출 실패:', error)
            return []
        }

        return data
    } catch (error) {
        console.error('getTutorHistory 호출 실패:', error)
        return []
    }
}
